import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';
import { useNavigate } from 'react-router-dom';

/**
 * TrialCountdownBanner
 * ---------------------------------------------------------------------------
 * Slim banner shown at the top of the Admin Dashboard so nursery owners
 * always know how much of their 14-day free trial is left. Auto-hides for
 * paid subscriptions. Colour and urgency ramp up as the deadline nears:
 *
 *   • >7 days left  → calm teal, "N days left in your free trial"
 *   • 3-7 days left → amber, softer nudge
 *   • ≤2 days left  → red, includes "Add payment method" CTA
 *   • Expired       → red-solid, "Trial ended — reactivate to keep going"
 *
 * The banner is dismissible only within a session (never permanently) —
 * this is the equivalent of an unread envelope in your inbox: important
 * enough that we don't let people permanently silence it.
 */
export function TrialCountdownBanner() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('kw_trial_banner_dismissed') === '1';
    } catch (_) {
      return false;
    }
  });
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const res = await api.getMyTrialStatus();
      setStatus(res);
    } catch (_) {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Poll every 30 minutes so the day counter keeps up while an admin
    // has the tab open all afternoon.
    const t = setInterval(refresh, 30 * 60 * 1000);
    return () => clearInterval(t);
  }, [refresh]);

  if (!status) return null;
  const isExpiredLike = status.is_expired || status.state === 'grace' || status.state === 'locked';
  if (!status.on_trial && !isExpiredLike) return null;
  if (dismissed && !isExpiredLike) return null; // never allow dismiss when expired/locked

  // Colour + copy ramp
  const daysLeft = status.days_left ?? 0;
  const hoursLeft = status.hours_left ?? 0;
  const graceDaysLeft = status.grace_days_left ?? 0;
  const state = status.state || (status.is_expired ? 'grace' : (status.on_trial ? 'trialing' : 'active'));
  let tone;
  let title;
  let hint;
  if (state === 'locked') {
    // Grace period is over — hard-lock. Firm red.
    tone = 'red-solid';
    title = '🔒 Your workspace is locked';
    hint = 'Reactivate a plan to restore full access. Your data is safe and waiting.';
  } else if (state === 'grace') {
    // Trial expired, still in 14-day read-only grace. Amber, friendly.
    tone = 'amber';
    title = `📖 Trial ended — read-only mode (${graceDaysLeft} day${graceDaysLeft === 1 ? '' : 's'} left to export or reactivate)`;
    hint = 'You can still view everything and download reports. Reactivate any time to resume bookings and check-ins.';
  } else if (status.is_expired) {
    // Legacy path (before backend adds `state`); treat as grace.
    tone = 'amber';
    title = '📖 Trial ended — read-only mode';
    hint = 'You can still view everything and download reports. Reactivate any time to resume bookings and check-ins.';
  } else if (daysLeft <= 0 && hoursLeft > 0) {
    tone = 'red';
    title = `⏰ Today's the last day of your free trial (${hoursLeft}h left)`;
    hint = 'Add a payment method before midnight to avoid interruption.';
  } else if (daysLeft <= 2) {
    tone = 'red';
    title = `⏰ ${daysLeft} day${daysLeft === 1 ? '' : 's'} left in your free trial`;
    hint = 'Add a payment method to keep bookings, payroll and moments running.';
  } else if (daysLeft <= 7) {
    tone = 'amber';
    title = `🕒 ${daysLeft} days left in your free trial`;
    hint = "You've done the hard part. Pick a plan when you're ready — no auto-charge.";
  } else {
    tone = 'teal';
    title = `✨ ${daysLeft} days left in your free trial`;
    hint = "Explore everything Kids WiseApp offers — no card needed.";
  }

  const styles = {
    teal:      { bg: 'hsl(190 65% 96%)', border: 'hsl(190 55% 78%)', text: 'hsl(200 55% 25%)', btnBg: 'hsl(190 65% 45%)', btnText: 'white' },
    amber:     { bg: 'hsl(38 95% 96%)',  border: 'hsl(38 88% 78%)',  text: 'hsl(30 75% 25%)',  btnBg: 'hsl(30 80% 45%)',  btnText: 'white' },
    red:       { bg: 'hsl(0 75% 96%)',   border: 'hsl(0 65% 78%)',   text: 'hsl(0 60% 30%)',   btnBg: 'hsl(0 65% 45%)',   btnText: 'white' },
    'red-solid': { bg: 'hsl(0 65% 40%)', border: 'hsl(0 65% 30%)',   text: 'white',            btnBg: 'white',            btnText: 'hsl(0 65% 35%)' },
  }[tone];

  const goBilling = () => {
    navigate('/admin?tab=billing');
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="trial-countdown-banner"
      data-tone={tone}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.55rem 0.9rem',
        background: styles.bg,
        borderBottom: `1px solid ${styles.border}`,
        color: styles.text,
        fontSize: '0.85rem',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontWeight: 700, marginBottom: 2 }} data-testid="trial-banner-title">{title}</div>
        <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>{hint}</div>
      </div>
      <button
        type="button"
        onClick={goBilling}
        style={{
          background: styles.btnBg,
          color: styles.btnText,
          border: 'none',
          padding: '0.35rem 0.85rem',
          borderRadius: 6,
          fontSize: '0.78rem',
          fontWeight: 700,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
        data-testid="trial-banner-upgrade"
      >
        {isExpiredLike ? 'Reactivate now' : 'Choose a plan'}
      </button>
      {!isExpiredLike && (
        <button
          type="button"
          onClick={() => {
            setDismissed(true);
            try { sessionStorage.setItem('kw_trial_banner_dismissed', '1'); } catch (_) {}
          }}
          style={{
            background: 'transparent',
            color: styles.text,
            border: 'none',
            padding: '0.2rem 0.4rem',
            fontSize: '1.05rem',
            cursor: 'pointer',
            opacity: 0.6,
          }}
          data-testid="trial-banner-dismiss"
          title="Hide for this session"
          aria-label="Dismiss trial banner for this session"
        >
          ×
        </button>
      )}
    </div>
  );
}

export default TrialCountdownBanner;
